import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Music, Briefcase, Megaphone } from 'lucide-react';

const identities = [
  {
    id: 'artist',
    title: 'MUSIC ARTIST',
    subtitle: 'Release & Promotion Visuals',
    icon: Music,
    description: 'Cover art, streaming graphics, and promotional assets built to amplify your release.'
  },
  {
    id: 'business',
    title: 'SMALL BUSINESS',
    subtitle: 'Brand & Marketing Design',
    icon: Briefcase,
    description: 'Logos, collateral, and visual systems that scale with your brand.'
  },
  {
    id: 'events',
    title: 'EVENTS & PROMOTION',
    subtitle: 'Campaign & Event Visuals',
    icon: Megaphone,
    description: 'Flyers, banners, and large-format promotion that demands attention.'
  }
];

// Smooth, professional easing
const smoothEase = [0.4, 0, 0.2, 1];

const SceneIdentityGate = ({ selectedIdentity, onSelect }) => {
  const sectionRef = useRef(null);
  
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"]
  });
  
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);

  return (
    <section 
      ref={sectionRef}
      className="scene relative min-h-screen flex items-center justify-center py-32 overflow-hidden"
      id="identity-gate"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-background" />
      
      <motion.div 
        className="relative z-10 w-full max-w-6xl mx-auto px-6"
        style={{ opacity }}
      >
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: smoothEase }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="font-body text-sm tracking-[0.3em] text-accent/80 uppercase mb-5">
            SELECT YOUR PATH
          </p>
          <h2 className="font-display text-hero text-foreground mb-6">
            WHAT ARE YOU BUILDING?
          </h2>
          <div className="gold-line w-16 mx-auto" />
        </motion.div>
        
        {/* Identity options */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {identities.map((identity, index) => {
            const Icon = identity.icon;
            const isSelected = selectedIdentity === identity.id;
            
            return (
              <motion.button
                key={identity.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ 
                  duration: 0.7, 
                  delay: index * 0.1,
                  ease: smoothEase
                }}
                viewport={{ once: true }}
                onClick={() => onSelect(identity.id)}
                className={`identity-btn group text-left ${isSelected ? 'selected' : ''}`}
              >
                {/* Icon */}
                <div className={`mb-6 transition-colors duration-400 ${isSelected ? 'text-accent' : 'text-muted-foreground/60 group-hover:text-foreground/80'}`}>
                  <Icon className="w-10 h-10" strokeWidth={1.2} />
                </div>
                
                {/* Title */}
                <h3 className={`font-display text-2xl md:text-3xl mb-2 transition-colors duration-400 ${isSelected ? 'text-accent' : 'text-foreground'}`}>
                  {identity.title}
                </h3>
                
                {/* Subtitle */}
                <p className="font-body text-xs tracking-wider text-muted-foreground/60 uppercase mb-4">
                  {identity.subtitle}
                </p>
                
                {/* Description */}
                <p className="font-body text-sm text-muted-foreground/70 leading-relaxed">
                  {identity.description}
                </p>
                
                {/* Selection indicator - Subtle */}
                <div className={`absolute bottom-0 left-0 h-[2px] bg-accent transition-all duration-500 ${isSelected ? 'w-full' : 'w-0 group-hover:w-1/4'}`} 
                     style={{ transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)' }} />
              </motion.button>
            );
          })}
        </div>
        
        {/* Selected indicator - Minimal */}
        {selectedIdentity && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, ease: smoothEase }}
            className="text-center mt-12"
          >
            <p className="font-body text-xs tracking-wider text-muted-foreground/50">
              SELECTED: <span className="text-accent/80 uppercase">{selectedIdentity.replace('-', ' ')}</span>
            </p>
          </motion.div>
        )}
      </motion.div>
    </section>
  );
};

export default SceneIdentityGate;
